import { DateTime } from 'luxon';
import React, { use, useEffect, useState } from 'react';
import { Col, Container, Row, ToggleButton } from 'react-bootstrap';

import { useTranslation } from 'react-i18next';
import MultiselectBox from '../../Components/MultiselectBox';
import { UserPreferencesContext } from '../../Contexts/UserPreferencesContext';
import DateRange from '../../DateRange';
import { getLotListFilters, persistLotListFilters } from "../../Utilities/SessionStorage";
import { useBusinessUnits } from '../../Utilities/useBusinessUnits';
import { convertDateToJsonFormat } from '../../Utilities/useDateUtils';
import { useLines } from '../../Utilities/useLines';
import LotListTableSelector from './LotListTableSelector';

function LotList({ status = "", tableName }) {
    const { t } = useTranslation();

    // Set from date to last month
    const getFromDate = () => {
        let fromDate = DateTime.now()
            .minus({ months: 2 })
            .startOf('day')
            .startOf('month');
        return fromDate;
    };

    const [dateFilter, setDateFilter] = useState({
        from: getLotListFilters()?.dateFilter?.from ?? convertDateToJsonFormat(getFromDate()),
        to: null
    });

    const [includeGraded, setIncludeGraded] = useState(false);
    const [loading, setLoading] = useState(true);
    const { loading: loadingBusinessUnits, businessUnits } = useBusinessUnits();
    const { loading: loadingUserPreferences, userPreferences } = use(UserPreferencesContext);
    const [selectedBusinessUnits, setSelectedBusinessUnits] = useState();
    const [businessUnitFilterInitialized, setBusinessUnitFilterInitialized] = useState(false);
    const [userSelectedBusinessUnits, setUserSelectedBusinessUnits] = useState(false);
    const { loading: loadingLines, lines } = useLines(selectedBusinessUnits ?? []);
    const [selectedLines, setSelectedLines] = useState([]);
    const [lineFilterInitialized, setLineFilterInitialized] = useState(false);

    useEffect(() => {
        const filterItems = getLotListFilters();

        if (filterItems?.includeGraded) {
            setIncludeGraded(filterItems.includeGraded);
        }
        if (filterItems?.dateFilter) {
            setDateFilter(filterItems.dateFilter);
        }
    }, []);

    useEffect(() => {
        if (businessUnits.length === 0)
            return;

        const filterItems = getLotListFilters();

        // Default business units and lines are stored in both user prefs and session so we need an order of priority

        // Prioritise session storage
        if (filterItems?.businessUnits) {
            setSelectedBusinessUnits(businessUnits.filter(b => filterItems.businessUnits.includes(b.businessUnitId)));
        }
        // Fallback to user preferences
        else if (userPreferences?.lotList?.defaultBusinessUnits != null) {
            setSelectedBusinessUnits(businessUnits.filter(b => userPreferences.lotList.defaultBusinessUnits.includes(b.code)));
        }

        setBusinessUnitFilterInitialized(true);

    }, [businessUnits, userPreferences]);

    useEffect(() => {
        if (lines.length === 0)
            return;

        const filterItems = getLotListFilters();

        if (filterItems?.lines) {
            setSelectedLines(lines.filter(l => filterItems.lines.includes(l.lineId)));
        }
        else if (userPreferences?.lotList?.defaultLines != null) {
            setSelectedLines(lines.filter(l => userPreferences.lotList.defaultLines?.includes(l.productionLine)));
        }

        setLineFilterInitialized(true);

    }, [lines, userPreferences]);

    useEffect(() => {
        setLoading(!businessUnitFilterInitialized
            || !lineFilterInitialized
            || loadingBusinessUnits
            || loadingLines
            || loadingUserPreferences);
    }, [businessUnitFilterInitialized, lineFilterInitialized, loadingBusinessUnits, /*loadingLines,*/ loadingUserPreferences]);

    const handleIncludeGraded = (e) => {
        let includeGradedValue = e.target.checked;
        persistLotListFilters('includeGraded', includeGradedValue);
        setIncludeGraded(includeGradedValue);
    };

    useEffect(() => {
        persistLotListFilters('dateFilter', dateFilter);
    }, [dateFilter]);

    useEffect(() => {
        if (!businessUnitFilterInitialized || !selectedBusinessUnits)
            return;

        if (userSelectedBusinessUnits) {
            persistLotListFilters('businessUnits', selectedBusinessUnits.map(x => x.businessUnitId));
            setUserSelectedBusinessUnits(false);
        }
    }, [businessUnitFilterInitialized, selectedBusinessUnits, userSelectedBusinessUnits]);

    useEffect(() => {
        if (!lineFilterInitialized || !selectedLines)
            return;

        persistLotListFilters('lines', selectedLines.map(x => x.lineId));
    }, [lineFilterInitialized, selectedLines]);

    const handleSelectBusinessUnits = (value) => {
        setUserSelectedBusinessUnits(true);
        setSelectedBusinessUnits(value)
    }

    return (
        <Container fluid className="mb-5">
            <Row className="mb-3 gy-2 d-flex align-items-bottom">
                <Col md="6">
                    <Container fluid>
                        <Row>
                            <Col md="6">
                                <DateRange dateFilter={dateFilter} setDateFilter={setDateFilter} />
                            </Col>
                            <Col md="3">
                                <MultiselectBox
                                    name={t("LotList.Filters.Business")}
                                    options={businessUnits}
                                    selectedOptions={selectedBusinessUnits ?? []}
                                    setSelectedOptions={handleSelectBusinessUnits}
                                    map="code" />
                            </Col>
                            <Col md="2">
                                <MultiselectBox
                                    name={t("LotList.Filters.Line")}
                                    options={lines}
                                    selectedOptions={selectedLines ?? []}
                                    setSelectedOptions={setSelectedLines}
                                    map="productionLine" />
                            </Col>
                        </Row>
                    </Container>
                </Col>

                <Col className="ms-auto">
                    <div className="d-flex justify-content-end">
                        <ToggleButton
                            id="includeGraded"
                            type="checkbox"
                            variant="outline-primary"
                            checked={includeGraded}
                            value={includeGraded}
                            onChange={handleIncludeGraded}
                        >
                            {includeGraded
                                ? t("LotList.Filters.ExcludeGraded")
                                : t("LotList.Filters.IncludeGraded")
                            }
                        </ToggleButton>
                    </div>
                </Col>
            </Row>

            <LotListTableSelector tableName={tableName} tableFilters={{ selectedBusinessUnits: selectedBusinessUnits, selectedLines: selectedLines, status: status, dateFilter: dateFilter, includeGraded: includeGraded }} loadingFilters={loading} />

        </Container >
    );
}

export default LotList;